import { useCallback, useEffect, useRef, useState } from 'react';
import { SQUARE_APPLICATION_ID, SQUARE_LOCATION_ID, loadSquareSdk } from './square';

/**
 * Mounts a Square card input — a secure, PCI-compliant iframe — into a container
 * element and exposes a `tokenize()` call. Raw card details never enter React
 * state or our own DOM; the SDK owns them end to end and returns only a
 * single-use token.
 *
 * Pass `enabled` so the card mounts only once its container is on screen (i.e.
 * on the payment step). Cleanly destroys the card on unmount, and is resilient
 * to React StrictMode's double-invoke in development.
 */
export const useSquareCard = (enabled) => {
  const containerRef = useRef(null);
  const cardRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;
    setError(null);
    setReady(false);

    (async () => {
      try {
        const Square = await loadSquareSdk();
        if (cancelled) return;

        const payments = Square.payments(SQUARE_APPLICATION_ID, SQUARE_LOCATION_ID);
        // Style the hosted card field to match the app (navy focus, slate text).
        const card = await payments.card({
          style: {
            input: { color: '#1e293b', fontSize: '14px' },
            '.input-container': { borderColor: '#cbd5e1', borderRadius: '8px' },
            '.input-container.is-focus': { borderColor: '#00398f' },
            '.input-container.is-error': { borderColor: '#dc2626' },
            '.message-text.is-error': { color: '#dc2626' },
          },
        });
        // A StrictMode remount (or a fast step change) may have torn us down
        // while `card()` was resolving — throw the orphaned instance away.
        if (cancelled) {
          card.destroy?.();
          return;
        }

        await card.attach(containerRef.current);
        cardRef.current = card;
        setReady(true);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Could not load the payment form.');
      }
    })();

    return () => {
      cancelled = true;
      setReady(false);
      if (cardRef.current) {
        cardRef.current.destroy?.();
        cardRef.current = null;
      }
    };
  }, [enabled]);

  /** Tokenize the entered card. Resolves with a single-use source token. */
  const tokenize = useCallback(async () => {
    if (!cardRef.current) throw new Error('The payment form is still loading.');
    const result = await cardRef.current.tokenize();
    if (result.status !== 'OK') {
      const detail = result.errors?.[0]?.message;
      throw new Error(detail || 'Please check your card details and try again.');
    }
    return result.token;
  }, []);

  return { containerRef, ready, error, tokenize };
};
